import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../../core/apiClient.ts';

interface CurrencyRate { code: string; usd_rate: number | null; updated_at: string | null }

export function CurrencyRatesPage() {
  const [searchParams] = useSearchParams();
  const requestedCode = searchParams.get('code')?.trim().toUpperCase() ?? '';
  const [rows, setRows] = useState<CurrencyRate[]>([]);
  const [code, setCode] = useState(/^[A-Z]{3}$/.test(requestedCode) ? requestedCode : 'EUR');
  const [rate, setRate] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = async () => {
    setLoading(true);
    try { setRows(await api.get<CurrencyRate[]>('/api/currency-rates')); setError(''); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { void load(); }, []);

  const select = (value: string) => {
    setCode(value);
    const current = rows.find(r => r.code === value);
    setRate(current?.usd_rate == null ? '' : String(current.usd_rate));
    setSuccess('');
  };

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    const normalized = code.trim().toUpperCase();
    const value = Number(rate.trim().replace(',', '.'));
    if (!/^[A-Z]{3}$/.test(normalized) || !Number.isFinite(value) || value <= 0 || value > 1e9) {
      setError('Введіть код із 3 латинських літер та додатний курс до USD.');
      return;
    }
    setSaving(true); setError(''); setSuccess('');
    try {
      await api.put(`/api/currency-rates/${normalized}`, { usd_rate: value });
      setCode(normalized);
      await load();
      setSuccess(`Збережено: 1 ${normalized} = ${value} USD. Відкрийте статистику, щоб побачити перерахунок.`);
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setSaving(false); }
  };

  return <div className="page-body" style={{ maxWidth: 960 }}>
    <h1 style={{ fontSize: '1.5rem' }}>Курси валют</h1>
    <p className="text-muted" style={{ margin: '16px 0' }}>
      Статистика відображається в USD. Вкажіть, скільки доларів коштує 1 одиниця валюти кабінету.
      Поточний курс застосовується до всього вибраного періоду, включно з раніше завантаженими даними.
      USD завжди має курс 1.
    </p>
    <form onSubmit={save} className="glass-card" style={{ padding: 20, display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'end' }}>
      <label className="field">Оберіть валюту
        <select className="input" value={rows.some(r => r.code === code) ? code : ''} onChange={e => select(e.target.value)} disabled={loading || saving}>
          <option value="">Додати нову валюту</option>
          {rows.map(r => <option key={r.code} value={r.code}>{r.code}{r.usd_rate === null ? ' — курс не задано' : ''}</option>)}
        </select>
      </label>
      <label className="field">Код валюти
        <input className="input" value={code} onChange={e => select(e.target.value.toUpperCase())} maxLength={3} pattern="[A-Za-z]{3}" placeholder="PLN" required disabled={saving} />
      </label>
      <label className="field">1 {code || 'одиниця'} = USD
        <input className="input" inputMode="decimal" value={code === 'USD' ? '1' : rate} onChange={e => setRate(e.target.value)} placeholder="Наприклад, 1,17" required disabled={saving || code === 'USD'} />
      </label>
      <button className="btn btn--primary" disabled={saving || loading || code === 'USD'}>{saving ? 'Збереження…' : 'Зберегти курс'}</button>
    </form>
    {error && <div role="alert" className="notification notification--error" style={{ marginTop: 16 }}>{error} <button className="btn btn--secondary btn--sm" onClick={load} disabled={loading || saving}>Оновити список</button></div>}
    {success && <div role="status" className="notification notification--success" style={{ marginTop: 16 }}>{success}</div>}
    {loading ? <p role="status">Завантаження…</p> : <div className="table-wrapper" style={{ marginTop: 24 }}>
      <table className="data-table"><thead><tr><th>Валюта</th><th>USD за 1 одиницю</th><th>Дія</th></tr></thead>
        <tbody>{rows.map(row => <tr key={row.code}>
          <td className="font-mono">{row.code}</td>
          <td>{row.usd_rate === null ? 'Потрібно задати курс' : row.usd_rate}</td>
          <td>{row.code === 'USD' ? 'Фіксований курс' : <button className="btn btn--secondary btn--sm" onClick={() => select(row.code)} disabled={saving}>Редагувати</button>}</td>
        </tr>)}</tbody>
      </table>
    </div>}
    <p style={{ marginTop: 24 }}><Link to="/ads-stats">До статистики в USD</Link></p>
  </div>;
}
